<script lang="ts">
	import { attrs } from '#library/attrs.ts'
	import type { Blok, CardLite, Infousel } from '#library/components.schema.build.ts'
	import Render from '#components/render.svelte'
	import Link from '#components/link.svelte'
	import emblaCarouselSvelte from 'embla-carousel-svelte'
	import { setupTweenOpacity } from '#library/emblaTween.ts'
	import type { EmblaCarouselType } from 'embla-carousel'
	import Pagination from '#components/pagination.svelte'
	import CardAction from '#components/card_action.svelte'
	import Card from '#components/card.svelte'
	import { MediaQuery } from 'svelte/reactivity'

	interface Props {
		blok: Blok<Infousel>
	}

	let { blok }: Props = $props()
	let active = $state(0)
	let embla_api: EmblaCarouselType | undefined = $state()
	let { current } = $derived(new MediaQuery('min-width: 640px'))

	function onInit(event: CustomEvent) {
		embla_api = event.detail
		if (!embla_api) return

		const removeTweenOpacity = blok.featured ? setupTweenOpacity(embla_api) : () => {}

		embla_api.on('select', () => {
			active = embla_api?.selectedScrollSnap() ?? 0
		})

		embla_api.on('destroy', removeTweenOpacity)
	}

	function get_card_props(item: CardLite): CardLite {
		if (item.populate && typeof item.populate === 'object') {
			return {
				...item,
				theme: 'theme' in item.populate.content ? item.populate.content.theme : undefined,
				image: item.populate.content.photo,
				title: item.populate.name,
				subtitle: 'title' in item.populate.content ? item.populate.content.title : undefined,
				link: {
					type: 'internal',
					url: item.populate.full_slug
				}
			}
		}

		return item
	}
</script>

{#key blok._uid}
	<section class="infousel {blok.theme}" {...attrs(blok)}>
		<div class="contain">
			{#if blok.title?.text || blok.subtitle || blok.body?.content?.[0].content}
				<div>
					{#if blok.title?.text}
						<svelte:element this={blok.title?.tag || 'div'} class="title">
							{blok.title?.text}
						</svelte:element>
					{/if}
					{#if blok.subtitle}
						<div class="subtitle">{blok.subtitle}</div>
					{/if}
					{#if blok.body?.content?.[0].content}
						<div class="body">
							<Render content={blok.body} />
						</div>
					{/if}
				</div>
			{/if}
			{#if blok.action?.url && blok.action.text}
				<div>
					<Link href={blok.action.url} text={blok.action.text} target={blok.action.target} />
				</div>
			{/if}
			{#if blok.items?.length}
				<div
					class="viewport"
					use:emblaCarouselSvelte={{
						options: { align: blok.featured ? 'center' : 'start', loop: blok.featured },
						plugins: []
					} as never}
					onemblaInit={onInit}
				>
					<div class="items" class:featured={blok.featured}>
						{#each blok.items as item, i (item._uid)}
							{@const props = get_card_props(item)}
							<div class="item" class:featured={blok.featured} class:active={i === -1}>
								{#if item.shape}
									<Card {...props} href={props.link?.url} />
								{:else}
									<CardAction {...props} featured={blok.featured} noimage={blok.noimage} />
								{/if}
							</div>
						{/each}
						{#if !blok.featured && current}
							<div class="item"></div>
							<div class="item"></div>
						{/if}
					</div>
				</div>
			{/if}
		</div>
		{#if blok.items?.length}
			<div class="contain pagi" class:featured={blok.featured} class:hide={blok.nopagi}>
				<Pagination
					onprev={embla_api?.scrollPrev}
					onnext={embla_api?.scrollNext}
					onpage={embla_api?.scrollTo}
					items={blok.items.length}
					{active}
					alt={!blok.featured}
				/>
			</div>
		{/if}
	</section>
{/key}

<style lang="css">
	.infousel {
		background-color: var(--c-background);
		color: var(--c-foreground);
		padding-block: var(--padding-top, var(--padding-default)) var(--padding-bottom, var(--padding-default));

		:global {
			.pagination {
				grid-column: 1 / -1;
			}
		}
	}

	.contain {
		display: grid;

		@media (--from-mobile) {
			grid-template-columns: 2fr 1fr;
			gap: 4rem 2.5rem;
		}
	}

	.title {
		font: 600 var(--f-h2);
		letter-spacing: var(--ls-h2);
		text-wrap: balance;
	}

	.subtitle {
		color: var(--c-midground);
		font: 500 var(--f-h4);
		letter-spacing: var(--ls-h4);
		margin-block-start: 2.5rem;
	}

	.body {
		color: var(--c-midground);
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		margin-block-start: 1.5rem;
		max-width: 50ch;
	}

	.viewport {
		grid-column: 1 / -1;
		max-width: 92vw;

		@media (--from-mobile) {
			max-width: 90vw;
		}
	}

	.items {
		align-items: end;
		display: flex;
		grid-column: 1 / -1;
		/* gap: 0.75rem; */
		margin-block: 2.5rem;

		@media (--from-mobile) {
			/* gap: 2.5rem; */
			margin-block-start: 0;
		}

		&.featured {
			align-items: center;
			display: flex;
		}
	}

	.item {
		flex: 0 0 18rem;
		padding-inline: 0.5rem;

		@media (--from-mobile) {
			flex-basis: 22.5rem;
			padding-inline: 1rem;
		}

		&.active {
			flex-basis: 20rem;

			@media (--from-mobile) {
				flex-basis: 27rem;
			}
		}

		&.featured {
			align-items: center;
			display: flex;
			flex: 1 0 100%;
			min-height: 32rem;
			min-width: 0;
			max-width: min(92vw, 66.25rem);
			scroll-snap-align: center;
			user-select: none;
		}
	}

	.pagi.featured {
		:global(.pagination) {
			margin-inline: auto;
		}
	}

	.pagi.hide {
		@media (--from-laptop) {
			display: none;
		}
	}
</style>
